import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOptimizedQuery, createSupabaseQuery } from "@/hooks/useOptimizedQuery";

interface StickyNote {
  id: string;
  user_id: string;
  title: string;
  content: string;
  color: string;
  position: number;
  created_at: string;
  updated_at: string;
}

export const useStickyNotes = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const fetchNotes = createSupabaseQuery<StickyNote[]>(
    () => supabase
      .from('sticky_notes')
      .select('*')
      .eq('user_id', user?.id || '')
      .order('position', { ascending: true })
      .order('created_at', { ascending: false }),
    (data) => data || []
  );

  const {
    data: notes = [],
    isLoading: loading,
    refetch
  } = useOptimizedQuery<StickyNote[]>({
    queryKey: ['sticky_notes', user?.id],
    queryFn: fetchNotes,
    enabled: !!user,
  });

  const createNote = async (title: string = "Untitled Note", content: string = "", color: string = "#fbbf24") => {
    if (!user) return;

    try {
      const maxPosition = Math.max(...notes.map(n => n.position), -1);
      const { data, error } = await supabase
        .from('sticky_notes')
        .insert({
          user_id: user.id,
          title: title.trim() || "Untitled Note",
          content: content.trim(),
          color,
          position: maxPosition + 1
        })
        .select()
        .single();

      if (error) throw error;
      if (data) {
        // Update React Query cache
        queryClient.setQueryData(['sticky_notes', user.id], (old: StickyNote[] = []) => [...old, data]);
      }
      return data;
    } catch (error) {
      console.error('Error creating sticky note:', error);
    }
  };

  const updateNote = async (id: string, updates: Partial<Pick<StickyNote, 'title' | 'content' | 'color'>>) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('sticky_notes')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        // Update React Query cache
        queryClient.setQueryData(['sticky_notes', user.id], (old: StickyNote[] = []) =>
          old.map(note => note.id === id ? data : note)
        );
      }
    } catch (error) {
      console.error('Error updating sticky note:', error);
    }
  };

  const deleteNote = async (id: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('sticky_notes')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      // Update React Query cache
      queryClient.setQueryData(['sticky_notes', user.id], (old: StickyNote[] = []) =>
        old.filter(note => note.id !== id)
      );
    } catch (error) {
      console.error('Error deleting sticky note:', error);
    }
  };

  // Set up real-time subscription for sticky notes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('sticky_notes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sticky_notes',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Sticky note change:', payload);
          // Invalidate and refetch to ensure consistency
          queryClient.invalidateQueries({ queryKey: ['sticky_notes', user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  return {
    notes,
    loading,
    createNote,
    updateNote,
    deleteNote,
    refetch
  };
};